<script setup>
import DpIcon from './DpIcon';
import { useI18n } from 'vue-i18n'
import { isEmpty } from 'lodash';
import DpFlag from "./DpFlag";
import DpExpand from "./DpExpand";
import DpReadValues from "./DpReadValues";
import DpTable from './DpTable';
import DpButton from './DpButton';
import { ref, nextTick, watch, useSlots, computed } from 'vue';
import { ElOption } from "element-plus";
import { useJurisdictionSwitch } from "@dutypay/components-library";

const jurisdictionSwitch = useJurisdictionSwitch();
const { t } = useI18n();
const { locale } = useI18n({ useScope: 'global' });
const tKAAliases = [
  t('hs.jurisdiction.listValue-')
];
const slots = useSlots();
const props = defineProps({
  modelValue: { type: [Object, Array] },
  keyMap: { type: Object },
  title: { type: String },
  languageTag: { type: String },
  isEditSlotActive: { type: Boolean, default: false },
  isReadOnly: { type: Boolean, default: false },
  buttonLabel: { type: String, default: undefined },
  buttonIcon: { type: [Boolean, String], default: true }
});
const emit = defineEmits(['activate-edit-slot', 'deactivate-edit-slot']);

const hasEditMode = ref((props.isReadOnly) ? false : !props.isEditSlotActive);
const isBodyMounted = ref(false);
const getJurisdictionLanguage = computed(() => {
  return jurisdictionSwitch.getJurisdictionByLocale(props.languageTag);
})
const getHasDifferentSourceLanguage = computed(() => {
  return (!isEmpty(getJurisdictionLanguage.value) && getJurisdictionLanguage.value !== locale.value);
})

async function activateSlot() {
  isBodyMounted.value = true;
  await nextTick(() => {
    hasEditMode.value = true;
    emit('activate-edit-slot');
  });
}

function deactivateSlot() {
  hasEditMode.value = false;
  setTimeout(() => {
    isBodyMounted.value = false;
    emit('deactivate-edit-slot');
  }, 300);
}

watch(props.isEditSlotActive, (value) => {
  if (props.isReadOnly)
    return;
  if (value === true) {
    activateSlot();
  } else {
    deactivateSlot();
  }
})

</script>

<script>
export default {
  name: "DpReadEditDelete"
}
</script>

<template>
  <div class="dp-read-edit-delete">
    <div class="dp-read-edit-delete__title">
      <h4><slot name="title">{{ props.title }}</slot></h4>
    </div>
    <div class="dp-read-edit-delete__body">

      <div v-if="isBodyMounted">
        <DpExpand>
          <div v-show="hasEditMode && !props.isReadOnly">
            <slot></slot>
          </div>
        </DpExpand>
      </div>
      <DpExpand>
        <div v-show="!hasEditMode">
          <slot v-if="props.modelValue !== undefined" name="read" v-bind:data="props.modelValue">
            <DpNotification class="feedforward__source-language" v-if="getHasDifferentSourceLanguage" type="info">
              <div>{{ t('feedforward.sourceLanguage.info.message') }}</div>
              <div class="jurisdiction-language">
                <span><DpFlag :flagISOCode="getJurisdictionLanguage.flagISOCode"/></span>
                <span>{{ t(`hs.jurisdiction.listValue-${getJurisdictionLanguage.translationKeyId}`) }}</span>
              </div>
            </DpNotification>
            <DpTable v-if="Array.isArray(props.modelValue)" size="large" :data="props.modelValue" :columns="props.keyMap"></DpTable>
            <DpReadValues v-else :data="props.modelValue" :keyMap="props.keyMap"></DpReadValues>
          </slot>
          <slot v-else name="read"></slot>
        </div>
      </DpExpand>

    </div>
    <template v-if="!props.isReadOnly">
      <DpExpand>
        <div v-show="!hasEditMode" class="dp-read-edit-delete__controls">
          <DpButton tabindex="0" @click="activateSlot" @keypress="activateSlot">
            <span><DpIcon v-if="props.buttonIcon" :icon="(props.buttonIcon === true) ? 'edit' : props.buttonIcon"/>{{
                !isEmpty(props.buttonLabel) ? props.buttonLabel : t('edit.buttonLabel')
              }}</span>
          </DpButton>
        </div>
        <div v-show="hasEditMode && slots['delete'] !== undefined" class="dp-read-edit-delete__controls">
          <slot name="delete"></slot>
        </div>
      </DpExpand>
    </template>
  </div>
</template>

<style lang="scss">
.dp-read-edit-delete {
  $padding: .75rem 1.5rem;
  $border: 2px solid var(--use-theme__ui-gradient-1);

  border-radius: 8px;
  margin: 1rem 0;
  border: $border;
  background: var(--use-theme__background-color);

  &__title {
    border-bottom: $border;
    padding: $padding;
  }

  &__body {
    padding: $padding;
  }

  &__controls {
    border-top: $border;
    padding: $padding;
  }

  .dp-notification.feedforward__source-language .jurisdiction-language {
    color: var(--use-theme__font-color);
    margin-block-start: 1em;
  }
}

@for $index from 1 to 5 {
  .dp-nest.depth-#{$index} > .dp-read-edit-delete {
    border-color: var(--use-theme__ui-gradient-#{$index + 1});

    &__title,
    &__controls {
      border-color: var(--use-theme__ui-gradient-#{$index + 1});
    }
  }
}
</style>

<i18n>
{
  "ar-AE": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "لغة المصدر للبيانات المعروضة"
        }
      }
    }
  },
  "ar-EG": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "لغة المصدر للبيانات المعروضة"
        }
      }
    }
  },
  "ar-SA": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "لغة المصدر للبيانات المعروضة"
        }
      }
    }
  },
  "de-DE": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Ausgangs-Sprache der angezeigten Daten"
        }
      }
    }
  },
  "de-AT": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Ausgangs-Sprache der angezeigten Daten"
        }
      }
    }
  },
  "de-CH": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Ausgangs-Sprache der angezeigten Daten"
        }
      }
    }
  },
  "en-US": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Source language of the displayed data"
        }
      }
    }
  },
  "en-GB": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Source language of the displayed data"
        }
      }
    }
  },
  "it-IT": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Lingua di origine dei dati visualizzati"
        }
      }
    }
  },
  "fr-FR": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Langue source des données affichées"
        }
      }
    }
  },
  "es-ES": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Idioma de origen de los datos mostrados"
        }
      }
    }
  },
  "pl-PL": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Język źródłowy wyświetlanych danych"
        }
      }
    }
  },
  "nl-NL": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Brontaal van de weergegeven gegevens"
        }
      }
    }
  },
  "bg-BG": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Изходният език на показаните данни"
        }
      }
    }
  },
  "bs-BA": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Izvorni jezik prikazanih podataka"
        }
      }
    }
  },
  "da-DK": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Kildesprog for de viste data"
        }
      }
    }
  },
  "et-EE": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Kuvatavate andmete lähtekeel"
        }
      }
    }
  },
  "fi-FI": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Näytettävien tietojen lähdekieli"
        }
      }
    }
  },
  "el-GR": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Γλώσσα πηγής των εμφανιζόμενων δεδομένων"
        }
      }
    }
  },
  "he-IL": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "שפת המקור של הנתונים המוצגים"
        }
      }
    }
  },
  "ga-IE": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Teanga foinse na sonraí ar taispeáint"
        }
      }
    }
  },
  "ja-JP": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "表示されるデータのソース言語"
        }
      }
    }
  },
  "hr-HR": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Izvorni jezik prikazanih podataka"
        }
      }
    }
  },
  "lv-LV": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Parādīto datu avota valoda"
        }
      }
    }
  },
  "lt-LT": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Rodomų duomenų šaltinio kalba"
        }
      }
    }
  },
  "pt-PT": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Idioma de origem dos dados exibidos"
        }
      }
    }
  },
  "ro-RO": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Limba sursă a datelor afișate"
        }
      }
    }
  },
  "ru-RU": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Исходный язык отображаемых данных"
        }
      }
    }
  },
  "sv-SE": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Källspråk för de visade data"
        }
      }
    }
  },
  "sk-SK": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Zdrojový jazyk zobrazovaných údajov"
        }
      }
    }
  },
  "cs-CZ": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Zdrojový jazyk zobrazovaných dat"
        }
      }
    }
  },
  "tr-TR": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Görüntülenen verilerin kaynak dili"
        }
      }
    }
  },
  "hu-HU": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "A megjelenített adatok forrásnyelve"
        }
      }
    }
  },
  "fr-BE": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Langue source des données affichées"
        }
      }
    }
  },
  "fr-LU": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Langue source des données affichées"
        }
      }
    }
  },
  "mt-MT": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Lingwa tas-sors tad-dejta murija"
        }
      }
    }
  },
  "sl-SI": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Izvorni jezik prikazanih podatkov"
        }
      }
    }
  },
  "el-CY": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Γλώσσα πηγής των εμφανιζόμενων δεδομένων"
        }
      }
    }
  },
  "tr-CY": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Görüntülenen verilerin kaynak dili"
        }
      }
    }
  },
  "uk-UA": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "Вихідна мова відображених даних"
        }
      }
    }
  },
  "zh-CN": {
    "feedforward": {
      "sourceLanguage": {
        "info": {
          "message": "显示数据的源语言"
        }
      }
    }
  }
}
</i18n>
